import { Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import type { FieldDefinition } from 'sr-types/lib/search/v1/graphql';
import type { EntityName } from '../common/auth/Security';
import { SECURITY_ACTIONS, SecuredButton } from '../common/auth/Security';
import Confirm from '../common/components/Confirm';
import Loading from '../common/components/Loading';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { V } from '../common/Layout';
import AttributeGroupPreview from './AttributeGroupPreview';
import {
    Attribute,
    getAttributesByGroups,
    getObjectSortedByKeys,
    getSupportedItems,
    parseMetadataToFields
} from './attributes';
import { useFieldDefinitionData } from './attributesApi';
import AttributesEditorModal from './AttributesEditorModal';
import FieldDefinitionModal from './FieldDefinitionModal';

interface AttributePreviewProps {
    rawAttributes: Attribute[];
    entity: EntityName;
    onEditAttributesSave: (attributes: Attribute[], onSuccess: () => void) => void;
    entityId: string;
    isParentEntityLoading: boolean;
    fetchFromEntity?: string[];
    onAttributeAddition?: (
        attributeIds: string[],
        onSuccess: (newFieldDefinition: FieldDefinition) => any,
        newFieldDefinition: FieldDefinition,
        isTempId: boolean
    ) => void;
    onChangeAssociatedFieldDefinitionReferences?: (fieldDefinitionId: string) => void;

    showMessageAfterAttributeAdd?: boolean;
}

export default function AttributesPreview({
    rawAttributes,
    entity,
    fetchFromEntity = [],
    onEditAttributesSave,
    onAttributeAddition = undefined,
    entityId,
    isParentEntityLoading,
    onChangeAssociatedFieldDefinitionReferences = undefined,
    showMessageAfterAttributeAdd = true
}: AttributePreviewProps) {
    const { fieldDefinitionData, isFieldDefinitionLoading } = useFieldDefinitionData(fetchFromEntity);

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [attributesEditorGroup, setAttributesEditorGroup] = useState('');
    const [fieldDefinitionId, setFieldDefinitionId] = useState('');

    const addSecurityTarget = useMemo(() => ({ entityName: entity }), [entity]);

    const attributes = useMemo(() => {
        const attributes = [];
        const loadedFieldDefinitions = fieldDefinitionData?.results?.hits?.items;
        //In order to get more information about attributes like label, group, metadata, etc... we are mapping them with field definitions by id
        if (loadedFieldDefinitions) {
            loadedFieldDefinitions.forEach((field) => {
                const result = Array.isArray(rawAttributes)
                    ? rawAttributes.find((attribute) => attribute.fieldDefinitionReference.id === field.id)
                    : null;
                result && attributes.push({ ...field, ...result });
            });
        }

        return getSupportedItems(attributes);
    }, [rawAttributes, fieldDefinitionData]);

    const attributesByGroups = useMemo(() => {
        return getObjectSortedByKeys(getAttributesByGroups(attributes));
    }, [attributes]);

    const onEditAttributeSuccess = () => {
        setIsEditFormOpen(false);
    };

    const onSuccessfulAssociation = (
        attributeIds: string[],
        newFieldDefinition: FieldDefinition,
        isTempId: boolean
    ) => {
        onAttributeAddition?.(attributeIds, onAttributeAdditionSuccess, newFieldDefinition, isTempId);
    };

    const onAttributeAdditionSuccess = (newFieldDefinition: FieldDefinition) => {
        setIsModalOpen(false);

        if (!isEmpty(newFieldDefinition)) {
            const existingIds = fieldDefinitionData.results.hits.items;
            existingIds.push(newFieldDefinition);
            return existingIds;
        }

        return fieldDefinitionData.results.hits.items;
    };

    return (
        <V sx={{ p: 1, gap: 1, height: 'fit-content' }}>
            <SecuredButton
                securityAction={SECURITY_ACTIONS.EDIT}
                securityTarget={addSecurityTarget}
                sx={{ alignSelf: 'start' }}
                onClick={() => setIsModalOpen(true)}
            >
                <Icons.AddButton /> <I18n token={'attributes.title'} />
            </SecuredButton>
            {isFieldDefinitionLoading || isParentEntityLoading ? (
                <Loading size={'20px'} />
            ) : Object.keys(attributesByGroups).length > 0 ? (
                <V sx={{ gap: 2 }}>
                    {Object.keys(attributesByGroups).map((key) => (
                        <AttributeGroupPreview
                            attributes={parseMetadataToFields(attributesByGroups[key])}
                            enableEdit
                            entityName={entity}
                            onEdit={(group) => {
                                setIsEditFormOpen(true);
                                setAttributesEditorGroup(group);
                            }}
                            enableDelete
                            onDelete={(id) => {
                                setFieldDefinitionId(id);
                                setDeleteConfirmationOpen(true);
                            }}
                            id={attributesByGroups[key][0].id}
                            group={key}
                            key={key}
                        />
                    ))}
                </V>
            ) : (
                <Typography
                    variant="h6"
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <I18n token="attributes.noAttributes" />
                </Typography>
            )}
            {isModalOpen && (
                <FieldDefinitionModal
                    entityId={entityId}
                    entity={entity}
                    fetchFromEntity={fetchFromEntity}
                    open={isModalOpen}
                    showMessageAfterAttributeAdd={showMessageAfterAttributeAdd}
                    setIsOpen={setIsModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccessfulAssociation={onSuccessfulAssociation}
                    existingAttributes={attributes}
                />
            )}
            {isEditFormOpen && (
                <AttributesEditorModal
                    attributes={attributesByGroups[attributesEditorGroup]}
                    group={attributesEditorGroup}
                    isOpen
                    onCancel={() => {
                        setIsEditFormOpen(false);
                    }}
                    onSave={(attributes) => {
                        onEditAttributesSave(attributes, onEditAttributeSuccess);
                    }}
                />
            )}
            <Confirm
                open={deleteConfirmationOpen}
                title={<I18n token={'delete.confirm.title'} />}
                okLabelI18n="delete.confirm.title"
                body={
                    <Typography>
                        <I18n token={'delete.confirm.message'} />
                    </Typography>
                }
                onConfirm={() => {
                    onChangeAssociatedFieldDefinitionReferences(fieldDefinitionId);
                    setDeleteConfirmationOpen(false);
                }}
                onCancel={() => {
                    setDeleteConfirmationOpen(false);
                }}
            />
        </V>
    );
}
